import * as React from 'react';
import { CaseInitiationCard } from './CaseInitiationCard';
import { useNotification } from '@/hooks';
import useModal from '@/hooks/useModal';
import { ModalHeader, UploadDocument } from '@/components/dashboard';
import { Button, Typography } from '@/components/common';
import Icons from '@/const/icons';
import { GeneratedFilesHistory } from './GeneratedFilesHistory';
import { FilesSent } from './FilesSent';
import { CaseContext } from '@/context/CaseContext';
import { caseApplicantService } from '@/api/caseApplicant';
import {
	ApprovePhase,
	CaseType,
	DocumentRecordPayload,
	StatusPhase,
} from '@/interfaces';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
	auditService,
	awsService,
	GET_CASE_STEPS_STATUS,
	GET_COMPLAINT_USER_COUNTS,
	GET_DOCUMENT_RECORDS,
	GET_LAWYER_CASE_DETAILS,
	GET_VERSION_RECORDS,
} from '@/api';
import { complaintService } from '@/api/complaint';
import { getStep } from '@/lib';
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '@/components/ui/accordion';
import { InputRadio } from '@/components/form';
import { useForm } from 'react-hook-form';
import clsx from 'clsx';

export const CaseInitiation: React.FC<{
	casePhase: StatusPhase | undefined;
}> = ({ casePhase }) => {
	const queryClient = useQueryClient();
	const { register, getValues } = useForm();
	const { caseDetails, setSaveStatus } = React.useContext(CaseContext);
	const { toastError, toastSuccess } = useNotification();
	const { Modal, show, hide } = useModal();
	const [file, setFile] = React.useState<File | null>(null);
	const [openNestedItem, setOpenNestedItem] = React.useState<
		string | undefined
	>();
	const [isSending, setIsSending] = React.useState(false);

	const isComplaint = caseDetails?.case_type === 'CLASS_ACTION';

	const complaintStep = getStep('Complaint', casePhase?.steps);
	const demandLetterStep = getStep('Demand Letter', casePhase?.steps);

	const isApproved =
		complaintStep?.status === 'approved' ||
		demandLetterStep?.status === 'approved';

	const {
		data: documentRecords,
		isLoading,
		refetch,
	} = useQuery({
		queryKey: [GET_DOCUMENT_RECORDS, caseDetails?.id],
		queryFn: () =>
			caseApplicantService.getDocumentRecords(caseDetails?.id || ''),
		enabled: !!caseDetails?.id,
	});

	const { data: userCounts, refetch: refetchUserCounts } = useQuery({
		queryKey: [GET_COMPLAINT_USER_COUNTS, caseDetails?.id],
		queryFn: () =>
			complaintService.getComplaintUserCounts(
				caseDetails?.id || '',
				caseDetails?.case_type as CaseType
			),
		enabled: !!caseDetails,
		refetchOnWindowFocus: false,
	});

	// --------------- Generate List ---------------//

	const handleGenerateList = async () => {
		if (!caseDetails) return;
		setSaveStatus({ isSaving: true });

		try {
			await complaintService.getComplaint(
				caseDetails?.id as string,
				caseDetails?.case_type as CaseType,
				true
			);
			queryClient.invalidateQueries({
				queryKey: [GET_VERSION_RECORDS, caseDetails?.id],
			});

			toastSuccess('New user list successfully generated.');
		} catch (error) {
			toastError('An error has occurred. Please try again.');
		} finally {
			setSaveStatus({ isSaving: false });
		}
	};

	// ----------------- Upload and send document ----------------//

	const handleUpload = () => {
		setFile(null);
		refetchUserCounts();
		show();
	};

	const handleSend = async () => {
		if (!caseDetails || !file) return;
		setSaveStatus({ isSaving: true });
		setIsSending(true);
		try {
			const url = (await awsService.uploadImage(file)).data.data.location;

			const data: DocumentRecordPayload = {
				documentType: isComplaint ? 'Complaint' : 'Demand Letter',
				documentName: file.name,
				documentUrl: url,
				is_all_users: getValues('send_to')
					? getValues('send_to') === 'all_users'
					: true,
			};

			await caseApplicantService.postDocumentRecords(caseDetails?.id, data);
			if (!isApproved) await handleApprove(); // change the step status to completed/approved
			refetch();
			toastSuccess(
				`${
					isComplaint ? 'Complaint' : 'Demand Letter'
				} has been successfully sent`
			);

			hide();
		} catch (error) {
			toastError('An error has occurred. Please try again.');
		} finally {
			setSaveStatus({ isSaving: false });
			setIsSending(false);
		}
	};

	const handleApprove = async (): Promise<void> => {
		try {
			const caseId = caseDetails?.id || '';
			const data: ApprovePhase = {
				case_id: caseId,
				lawyer_stepper_id: casePhase?.fase_id ?? 0,
				stepper_form_id: isComplaint
					? complaintStep?.step_id ?? 0
					: demandLetterStep?.step_id ?? 0,
			};
			await auditService.postApprovePhase(data);
			queryClient.refetchQueries({ queryKey: [GET_CASE_STEPS_STATUS, caseId] });
			queryClient.refetchQueries({
				queryKey: [GET_LAWYER_CASE_DETAILS, caseId],
			});
		} catch (error) {
			toastError('There was an error approving the case section.');
		}
	};

	const isASwitchedCase = React.useMemo(() => {
		return (
			(caseDetails?.case_type === 'MASS_ACTION' &&
				documentRecords?.data.some(
					(item) => item.case_type === 'CLASS_ACTION'
				)) ||
			(caseDetails?.case_type === 'CLASS_ACTION' &&
				documentRecords?.data.some((item) => item.case_type === 'MASS_ACTION'))
		);
	}, [documentRecords, caseDetails?.case_type]);

	// If the case is Demand Letter now, show the history of Complaints
	// and if the case is Complaint now, show the history of Demand Letters
	const fileSentPreviouslyHistoryFiltered = React.useMemo(() => {
		return documentRecords?.data.filter(
			(item) => item.case_type !== caseDetails?.case_type
		);
	}, [documentRecords, caseDetails?.case_type]);

	//  Principal table
	const fileSentFiltered = React.useMemo(() => {
		return documentRecords?.data.filter(
			(item) => item.case_type === caseDetails?.case_type
		);
	}, [documentRecords, caseDetails?.case_type]);

	return (
		<div className="space-y-6 divide-y divide-grey-10">
			<div className="space-y-6">
				<CaseInitiationCard
					title="Download Client List"
					subtitle="Click to download the list of clients associated with this case. The file will include key client details needed for preparing the document."
					titleCard="Generate New Report"
					subtitleCard="Creates a snapshot of all currently active clients in this case."
					buttonLabel="Generate"
					onClick={handleGenerateList}
					buttonIcon={Icons.lightning}
				/>
				<GeneratedFilesHistory />
			</div>

			<div className="space-y-6 pt-8">
				{/* If the case is Demand Letter now, show the history of Complaints and if the case is Complaint now, show the history of Demand Letters */}
				{isASwitchedCase && (
					<div className="space-y-4">
						<Accordion
							type="single"
							collapsible
							value={openNestedItem}
							onValueChange={setOpenNestedItem}
						>
							<AccordionItem value="history" className="border-none">
								<AccordionTrigger
									className="hover:no-underline !py-0"
									classNameChevron="!text-opacity-black"
								>
									<Typography type="semibold-16" className="text-grey-40">
										{isComplaint ? 'Demand Letter' : 'Complaint'}
									</Typography>
								</AccordionTrigger>
								<AccordionContent className="pt-6">
									<FilesSent
										isComplaint={isComplaint}
										versionRecords={fileSentPreviouslyHistoryFiltered || []}
										isLoading={isLoading}
										isASwitchedCase
									/>
								</AccordionContent>
							</AccordionItem>
						</Accordion>
					</div>
				)}
				<CaseInitiationCard
					title={isComplaint ? 'Upload Complaint' : 'Upload Demand Letter'}
					subtitle={`Click to open the upload window. Please ensure you have reviewed the client list before submitting the ${
						isComplaint ? 'complaint' : 'demand letter'
					}.`}
					titleCard={isComplaint ? 'Upload Complaint' : 'Upload Demand Letter'}
					subtitleCard="Creates a snapshot of all currently active clients in this case."
					buttonLabel="Upload"
					onClick={handleUpload}
					buttonIcon={Icons.documentUpload}
				/>
				<FilesSent
					isComplaint={isComplaint}
					versionRecords={fileSentFiltered || []}
					isLoading={isLoading}
				/>
			</div>

			<Modal
				withXButton
				classNameCardContainer="max-w-[600px]"
				title={
					<ModalHeader
						title={'Upload documentation'}
						icon={Icons.filesEmptyState}
						classNameImage="!w-[61px] !h-[48px]"
						classNameContent="!justify-start"
					/>
				}
			>
				<UploadDocument
					id="document"
					typeDocument="document"
					file={file}
					setFile={setFile}
					classNameDescription="!max-w-[270px]"
					accept=".pdf"
					maxFileSizeInMB={25}
					errorMaxSizeMessage="The maximum file size is 25 MB."
				/>
				<div className="flex flex-col gap-y-6 justify-center items-center mt-8">
					{isComplaint && (
						<div className="flex items-center gap-x-6">
							<Typography type="semibold-14" className="text-black">
								Send to:
							</Typography>
							<InputRadio
								name="send_to"
								value={'all_users'}
								register={register}
								className="text-xs items-center"
								defaultChecked={true}
							>
								All Users (
								{
									// If there are no documents, show the number of new users
									documentRecords?.data.length === 0
										? userCounts?.data.newUsers
										: userCounts?.data?.totalUsers
								}
								)
							</InputRadio>
							<InputRadio
								name="send_to"
								value={'new_users'}
								register={register}
								className={clsx('text-xs items-center', {
									'opacity-50': documentRecords?.data.length === 0,
								})}
								disabled={documentRecords?.data.length === 0}
							>
								New Users ({userCounts?.data?.newUsers})
							</InputRadio>
						</div>
					)}
					<div className="flex items-center gap-x-2 justify-between">
						<Button
							label="Cancel"
							size="fit"
							decoration="secondary"
							onClick={hide}
						/>
						<Button
							label="Send"
							size="fit"
							decoration="primary"
							onClick={handleSend}
							loading={isSending}
							disabled={isSending || !file}
						/>
					</div>
				</div>
			</Modal>
		</div>
	);
};
